import { useEffect, useRef, useState, useCallback } from 'react';
import API_ENDPOINTS from '../../api/apiConfig';
import config from '../../api/config.js';

const useRecommendationsTracker = (testId: number | null) => {
    const [showRecommendItem, setShowRecommendItem] = useState<number[]>([]);
    const [clickId, setClickIdState] = useState<number | null>(null);

    // Используем ref для хранения актуального значения clickId
    const clickIdRef = useRef<number | null>(null);
    const setClickId = (id: number | null) => {
        setClickIdState(id);
        clickIdRef.current = id;
    };

    const recommendationRef = useRef<HTMLDivElement | null>(null);

    const isFullyShown = (target: HTMLElement) => {
        const wt = window.scrollY;
        const wh = window.innerHeight;
        const eh = target.offsetHeight;
        const et = target.getBoundingClientRect().top + window.scrollY;
        return et >= wt && et + eh <= wh + wt;
    };

    const isShown = (target: HTMLElement) => {
        const wt = window.scrollY;
        const wh = window.innerHeight;
        const eh = target.offsetHeight;
        const et = target.getBoundingClientRect().top + window.scrollY;
        return wt + wh >= et && wt + wh - eh * 2 <= et + (wh - eh);
    };

    // Сохранение элемента как просмотренного
    const saveShowRecommendItem = (target: HTMLElement) => {
        const id = parseInt(target.getAttribute('data-id') || '', 10);
        if (!showRecommendItem.includes(id)) {
            setShowRecommendItem(prev => [...prev, id]);
        }
        // Убираем класс, чтобы больше не отслеживать этот элемент
        target.classList.remove('js-recommend-item');
    };

    // Проверка видимых элементов
    const isRecommend = useCallback(() => {
        document.querySelectorAll('.js-recommend-item').forEach(item => {
            if (isFullyShown(item as HTMLElement)) {
                saveShowRecommendItem(item as HTMLElement);
            }
        });
    }, [showRecommendItem]);

    // Функция отправки статистики (используем актуальное значение testId и clickIdRef)
    const sendStat = useCallback(() => {
        if (showRecommendItem.length === 0) {
            // console.log('sendStat - No recommendations to send.');
            return false;
        }

        const data = {
            to: showRecommendItem,
            from: testId,
            click: clickIdRef.current,
        };

        // console.log('sendStat - Sending data:', data);

        const url = `${config.BASE_URL}${API_ENDPOINTS.recommendationsTracker}`;
        const queue = navigator.sendBeacon(url, JSON.stringify(data));

        if (queue) {
            // console.log('sendStat - Data sent successfully, state cleared');
            setShowRecommendItem([]);
            setClickId(null);
        } else {
            console.error('sendStat - Failed to send data');
        }

        return queue;
    }, [showRecommendItem, testId]);

    // Обработчик скролла
    const handleScroll = useCallback(() => {
        if (recommendationRef.current && isShown(recommendationRef.current)) {
            isRecommend();
        }
    }, [isRecommend]);

    // Обработчик изменения видимости документа
    const handleVisibilityChange = useCallback(() => {
        if (document.hidden) {
            sendStat();
        }
    }, [sendStat]);

    // Регистрируем обработчики один раз
    useEffect(() => {
        // При загрузке проверяем видимость
        if (recommendationRef.current) {
            isRecommend();
        }

        window.addEventListener('scroll', handleScroll);
        document.addEventListener('visibilitychange', handleVisibilityChange);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [handleScroll, handleVisibilityChange, isRecommend]);

    // Отправка статистики при выгрузке страницы
    useEffect(() => {
        const beforeUnloadHandler = () => sendStat();
        window.addEventListener('beforeunload', beforeUnloadHandler);
        return () => {
            window.removeEventListener('beforeunload', beforeUnloadHandler);
        };
    }, [sendStat]);

    // Отслеживаем изменения в DOM (например, динамически добавленные элементы)
    useEffect(() => {
        const observer = new MutationObserver(() => {
            if (recommendationRef.current) {
                isRecommend();
            }
        });

        if (recommendationRef.current) {
            observer.observe(recommendationRef.current, {
                childList: true,
                subtree: true,
            });
        }

        return () => {
            observer.disconnect();
        };
    }, [isRecommend]);

    return { recommendationRef, setClickId };
};

export default useRecommendationsTracker;
